/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILinkProps, Link } from '@fluentui/react/lib/Link';
import * as React from 'react';
import { getStylist } from 'src/core/themes/luisStylist';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { LuisId } from 'src/luis/types/Resource';

export type IntentsListProps = {
    /**
     * The intent list to be displayed.
     */
    intents: IntentMetadata[];

    /**
     * A callback for when an intent is clicked.
     */
    onIntentClick: (intentId: LuisId) => void;
};

const { styleComponent, styleDiv } = getStylist('Dashboard');

const StyledIntentsList = styleDiv('StyledIntentsList', theme => ({
    marginTop: theme.intentListItemSpacing
}));

const StyledLink = styleComponent<ILinkProps>(Link)('StyledLink', theme => ({
    display: 'block',
    width: 'fit-content',
    textDecoration: 'none',
    color: theme.correctColor,
    fontSize: theme.mediumFontSize,
    lineHeight: theme.largeLineHeight,
    letterSpacing: theme.mediumLetterSpacing,
    $nest: {
        '&:hover': {
            color: theme.correctColor,
            textDecoration: 'none'
        }
    }
}));

/**
 * Helper component to represent list of intents.
 */
export const IntentsList = (props: IntentsListProps) => {
    const { intents, onIntentClick } = props;

    return (
        <StyledIntentsList>
            {intents.map(intent => (
                <StyledLink key={intent.intentId} onClick={() => onIntentClick(intent.intentId)}>
                    {intent.intentName}
                </StyledLink>
            ))}
        </StyledIntentsList>
    );
};
